import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Skeleton } from "@/components/ui/skeleton";
import { getCatalogueEntry, resolveCatalogueSlug } from "content/catalogue";
import * as QueryKeys from "data";
import {
  AdminAccountDetails,
  Connector,
  MemberAccountDetails,
  Pipeline,
} from "data/models";
import { OpportunityRow } from "pages/sales-tools/opportunities/models";
import {
  fetchAdminConnectorById,
  fetchAdminPipelineById,
  fetchAdminUserDetails,
  fetchOrganisationDetailsAdmin,
} from "data/queries-admin";
import { fetchMemberUserDetails } from "data/queries-common";
import { fetchSalesOpportunity } from "data/queries-sales";
import _ from "lodash";
import { AdminOrganisationDetail } from "pages/geoscape-admin/organisation/organisation-details/utils";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import * as Routes from "routes";

// Hook for fetching organization data
const useOrganisationData = (organisationId: string) => {
  return useQuery({
    queryKey: [QueryKeys.adminOrganisationDetailsQuery, organisationId],
    queryFn: () => fetchOrganisationDetailsAdmin(organisationId),
    enabled: organisationId !== "",
  });
};

// Hook for fetching user data
const useAdminUserData = (accountId: string, isAdminPage: boolean) => {
  return useQuery({
    queryKey: [QueryKeys.adminAccountDetailsQuery, accountId],
    queryFn: () => fetchAdminUserDetails(accountId),
    enabled: accountId !== "" && isAdminPage,
  });
};

const useOrgMembershipData = (accountId: string, isAdminPage: boolean) => {
  return useQuery({
    queryKey: [QueryKeys.organisationMemberAccount, accountId],
    queryFn: () => fetchMemberUserDetails(accountId),
    enabled: !!accountId && !isAdminPage,
  });
};

// Hook for fetching connector data
const useConnectorData = (organisationId: string, connectorId: string) => {
  return useQuery({
    queryKey: [QueryKeys.adminConnector, connectorId],
    queryFn: () => fetchAdminConnectorById(organisationId, connectorId ?? ""),
    enabled: connectorId !== "" && organisationId !== "",
  });
};

// Hook for fetching pipeline data
const usePipelineData = (organisationId: string, pipelineId: string) => {
  return useQuery({
    queryKey: [QueryKeys.adminPipeline, pipelineId],
    queryFn: () => fetchAdminPipelineById(organisationId, pipelineId ?? ""),
    enabled: pipelineId !== "" && organisationId !== "",
  });
};

// Hook for fetching sales opportunity data
const useSalesOpportunityData = (opportunityId: string) => {
  return useQuery({
    queryKey: [QueryKeys.salesOpportunity, opportunityId],
    queryFn: () => fetchSalesOpportunity(opportunityId),
    enabled: opportunityId !== "",
  });
};

// Breadcrumb segment name mappings
const BREADCRUMB_NAMES: Record<string, string> = {
  "national-buildings-chatswood": "",
  "flood-data-demo": "",
  explorer: "Explorer",
  clip: "Clip",
  "api-keys": "Apps",
  admin: "Geoscape Admin",
  demos: "Demos & Prototypes",
  management: "Customer Management",
  security: "User Security",
  predictive:
    "Address Auto-complete Demo (Predictive API + Addresses API + Maps API)",
};

// Route redirect mappings
const ROUTE_REDIRECTS: Record<string, string> = {
  "/admin": "/admin/dashboard",
  "/admin/customer-management": "/admin/customer-management/accounts",
};

// Clean up release names (e.g., "Patch-Postcode-Fix-2024-2" -> "Patch Postcode Fix")
const formatReleaseName = (name: string): string => {
  return name
    .replace(/-\d{4}-\d+$/, "") // Remove year pattern
    .replace(/-/g, " "); // Convert dashes to spaces
};

// Format breadcrumb segment based on context
const formatBreadcrumbSegment = (
  segment: string,
  pathContext: string,
  segmentIndex: number,
  pathArray: string[],
  orgData?: AdminOrganisationDetail,
  userData?: AdminAccountDetails,
  connectorData?: Connector,
  pipelineData?: Pipeline,
  memberData?: MemberAccountDetails,
  opportunityData?: OpportunityRow,
): string => {
  // Handle catalogue routes - show the dataset name instead of the URL slug
  if (pathContext.includes("/catalogue/")) {
    const slug = resolveCatalogueSlug(segment);
    const catalogueEntry = slug ? getCatalogueEntry(slug) : undefined;
    if (catalogueEntry) {
      return catalogueEntry.displayName;
    }
  }

  // Handle admin organization routes - replace org ID with org name
  if (
    pathContext.includes("/admin/organisations/") &&
    orgData?.organisationId === segment
  ) {
    return orgData.name;
  }

  // On org management user role page we also have a user id to swap in
  if (
    pathContext.includes("/admin/organisations/") &&
    userData?.username === segment
  ) {
    return `${userData.firstName} ${userData.lastName}`;
  }

  if (
    pathContext.includes("/admin/organisations/") &&
    pathContext.includes("/connectors/") &&
    connectorData?.connectorId === segment
  ) {
    return `${connectorData.name}`;
  }

  if (
    pathContext.includes("/admin/organisations/") &&
    pathContext.includes("/pipelines/") &&
    pipelineData?.pipelineId === segment
  ) {
    return `${pipelineData.name}`;
  }

  if (
    pathContext.includes("/sales-tools/opportunities/") &&
    opportunityData?.opp_id === segment
  ) {
    return opportunityData.opp_name;
  }

  if (
    pathContext.includes("/settings/organisation/") &&
    memberData?.username === segment
  ) {
    return `${memberData.firstName} ${memberData.lastName}`;
  }

  // Handle admin user routes - replace user ID with username
  if (pathContext.includes("/admin/users/") && userData?.username === segment) {
    return `${userData.firstName} ${userData.lastName}`;
  }

  // Handle release management edit routes - format release names (only for the release name segment)
  if (
    pathContext.includes("/admin/release-management/") &&
    pathArray[segmentIndex - 1] === "release-management"
  ) {
    return formatReleaseName(segment);
  }

  // Handle breadcrumb name mappings
  if (BREADCRUMB_NAMES[segment] !== undefined) {
    return BREADCRUMB_NAMES[segment];
  }

  // Default to start case
  return _.startCase(segment);
};

// Generate link path with redirects applied
const generateLinkPath = (
  basePath: string,
  segment: string,
  segmentIndex: number,
  pathArray: string[],
): string => {
  let linkPath = basePath;

  // Apply route redirects
  if (ROUTE_REDIRECTS[linkPath]) {
    linkPath = ROUTE_REDIRECTS[linkPath];
  }

  // Special case: organization routes need /general suffix
  if (pathArray[segmentIndex - 1] === "organisations" && segmentIndex === 2) {
    linkPath += "/general";
  }

  return linkPath;
};

const NO_CRUMBS = [Routes.hub, Routes.whatsNew];

export const DynamicBreadcrumbsMenu = () => {
  const location = useLocation();
  const pathArray = location.pathname.substring(1).split("/");
  const isAdminPage = pathArray.includes("admin");

  // Extract IDs from URL
  const organisationId = location.pathname.includes(
    `${Routes.adminOrganisations}/`,
  )
    ? pathArray[2]
    : "";

  // A user ID might be in position [2] on a admin-user route, or in position [4] on an admin org route
  const getAccountId = () => {
    if (location.pathname.includes(`${Routes.adminUsers}/`)) {
      return pathArray[2];
    }

    if (
      location.pathname.includes(`${Routes.adminOrganisations}/`) &&
      pathArray[4] &&
      pathArray[3] == "members"
    ) {
      return pathArray[4];
    }

    if (location.pathname.includes(`/settings/organisation/`) && pathArray[4]) {
      return pathArray[4];
    }
    return "";
  };

  const accountId = getAccountId();
  const connectorId =
    location.pathname.includes(`${Routes.adminOrganisations}/`) &&
    location.pathname.includes(`/connectors/`)
      ? pathArray[5]
      : "";

  const pipelineId =
    location.pathname.includes(`${Routes.adminOrganisations}/`) &&
    !location.pathname.endsWith("create") &&
    location.pathname.includes(`/pipelines/`)
      ? pathArray[4]
      : "";

  const opportunityId =
    location.pathname.includes(`${Routes.salesToolsOpportunities}/`) &&
    !location.pathname.endsWith("create-opportunity")
      ? pathArray[2]
      : "";

  // Fetch data
  const orgQuery = useOrganisationData(organisationId);
  const userQuery = useAdminUserData(accountId, isAdminPage);
  const connectorQuery = useConnectorData(organisationId, connectorId);
  const pipelineQuery = usePipelineData(organisationId, pipelineId);
  const memberQuery = useOrgMembershipData(accountId, isAdminPage);
  const opportunityQuery = useSalesOpportunityData(opportunityId);

  const isLoading =
    orgQuery.isLoading ||
    userQuery.isLoading ||
    connectorQuery.isLoading ||
    opportunityQuery.isLoading;

  const noBreadCrumb = NO_CRUMBS.includes(location.pathname);

  if (noBreadCrumb) {
    return null;
  }

  if (isLoading) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem className="hidden md:block">
            <Skeleton className="h-4 w-12" />
          </BreadcrumbItem>
          {pathArray.map((_, index) => (
            <span key={`skeleton-${index}`} className="contents">
              <BreadcrumbSeparator className="hidden md:block" />
              <BreadcrumbItem>
                <Skeleton className="h-4 w-24" />
              </BreadcrumbItem>
            </span>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden md:block">
          <BreadcrumbLink asChild>
            <Link to={Routes.hub}>Home</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {pathArray.map((segment, index) => {
          const isLastSegment = index === pathArray.length - 1;
          const basePath = "/" + pathArray.slice(0, index + 1).join("/");

          // Format the display name
          const displayName = formatBreadcrumbSegment(
            segment,
            location.pathname,
            index,
            pathArray,
            orgQuery.data,
            userQuery.data,
            connectorQuery.data,
            pipelineQuery.data,
            memberQuery.data,
            opportunityQuery.data,
          );

          // Skip segments with empty display names
          if (!displayName) {
            return null;
          }

          // Last segment is the current page
          if (isLastSegment) {
            return (
              <span key={index} className="contents">
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  <BreadcrumbPage>{displayName}</BreadcrumbPage>
                </BreadcrumbItem>
              </span>
            );
          }

          // Generate link path with redirects
          const linkPath = generateLinkPath(
            basePath,
            segment,
            index,
            pathArray,
          );

          // Admin root is not clickable
          if (basePath === "/admin") {
            return (
              <span key={index} className="contents">
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem className="hidden md:block">
                  <span className="text-muted-foreground">
                    {displayName}
                  </span>
                </BreadcrumbItem>
              </span>
            );
          }

          return (
            <span key={index} className="contents">
              <BreadcrumbSeparator className="hidden md:block" />
              <BreadcrumbItem className="hidden md:block">
                <BreadcrumbLink asChild>
                  <Link to={linkPath}>{displayName}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </span>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
